import { useState } from 'react'
import { MdPlayArrow } from 'react-icons/md'

import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IAsset, IAssetTag } from '~/types/assets-management'

import ActionMenu from '~/components/ActionMenu'
import { AssetStatusBadge } from '~/components/Badges'
import CreatedByCellRenderer from '~/components/CellRenderers/CreatedByCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import DoubleBadge from '~/components/DoubleBadge'
import FilePreviewImage from '~/components/FilePreviewImage'
import FilePreviewModal from '~/components/FilePreviewModal'
import Tooltip from '~/components/Tooltip'
import { ASSET_STATUS_LABEL_MAPPING, MAX_TAGS_ITEM } from '~/const'
import { ASSET_STATUS } from '~/types/assets-management'
import { getSingleValueTags } from '~/utils/asset'
import { disabledComparator } from '~/utils/comparators'
import { actionsColDef, createdByColDef, lastUpdateColDef, textColDef } from '~/utils/table'

const AssetStatusRenderer = ({ data }: ICellRendererParams<IAsset>): ReactElement => {
  if (data) {
    const { status } = data

    return (
      <div className="badges-container">
        <AssetStatusBadge size="medium" value={status} />
      </div>
    )
  }

  return <div />
}

const AssetPreviewRenderer = ({ data }: ICellRendererParams<IAsset>): ReactElement => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleModalState = (value: boolean) => () => setIsModalOpen(value)

  if (data?.files) {
    return (
      <>
        <button className="asset-preview-wrapper" onClick={handleModalState(true)}>
          {data.files[0].mime_type.includes('video') && (
            <div className="overlay-play-button">
              <MdPlayArrow color="#f9f9f9" size={10} />
            </div>
          )}
          <FilePreviewImage mimeType={data.files[0].mime_type} src={data.files[0].download_url} />
        </button>
        <FilePreviewModal
          downloadUrl={data.files[0].download_url}
          fileType={data.files[0].mime_type}
          isOpen={isModalOpen}
          onClose={handleModalState(false)}
        />
      </>
    )
  }

  return <div />
}

const PropertiesRenderer = ({ value }: ICellRendererParams<IAsset, IAsset['tags']>): ReactElement => {
  const getTags = (): IAssetTag[] => {
    if (!value) return []

    return getSingleValueTags(value)
  }

  return (
    <div className="properties-cell-renderer">
      {getTags()
        .slice(0, MAX_TAGS_ITEM)
        .map((tag: IAssetTag) =>
          tag.value.map((tagValue, index) => (
            <DoubleBadge key={`${tag}-${tagValue}-${index}`} keyTag={tag.key} valueTag={tagValue} />
          ))
        )}
      {value && value.length > MAX_TAGS_ITEM ? (
        <Tooltip
          align={{ points: ['br', 'tr'] }}
          className="assets-management-tags-tooltip"
          overlay={
            <div className="badges-wrapper">
              {getTags()
                .slice(MAX_TAGS_ITEM)
                .map((tag: IAssetTag) =>
                  tag.value.map((tagValue, index) => (
                    <DoubleBadge key={`${tag}-${tagValue}-${index}`} keyTag={tag.key} valueTag={tagValue} />
                  ))
                )}
            </div>
          }
          placement="top"
          trigger="hover"
        >
          <div className="hidden-tags-counter">+{value.length - MAX_TAGS_ITEM}</div>
        </Tooltip>
      ) : null}
    </div>
  )
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IAsset>): ReactElement => {
  if (data) {
    const { id: assetId } = data
    const { handleRedirect } = context
    const { handleEditAsset } = context

    const menu: Menu = {
      actions: [
        {
          action: () => handleEditAsset(assetId),
          label: 'Edit asset',
        },
      ],
      navigations: [
        {
          action: () => handleRedirect(`/dynamic-screens/assets-management/${assetId}`),
          label: 'Display asset',
          type: 'internal',
        },
      ],
    }

    return <ActionMenu menu={menu} placement="top" />
  }

  return <div />
}

const columns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'name',
    flex: 1,
    headerName: 'Asset',
    minWidth: 300,
    suppressHeaderMenuButton: true,
  }),
  {
    cellRenderer: AssetPreviewRenderer,
    field: 'files',
    headerName: 'Preview',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
    width: 120,
  },
  {
    cellRenderer: AssetStatusRenderer,
    comparator: () => 0,
    field: 'status',
    filter: 'agSetColumnFilter',
    filterParams: {
      valueFormatter: (params: ValueFormatterParams) => ASSET_STATUS_LABEL_MAPPING[params.value as ASSET_STATUS],
      values: Object.values(ASSET_STATUS),
    },
    headerName: 'Status',
    menuTabs: ['filterMenuTab'],
    minWidth: 150,
    resizable: false,
    width: 180,
  },
  {
    cellRenderer: PropertiesRenderer,
    field: 'tags',
    flex: 1,
    headerName: 'Properties',
    minWidth: 200,
    sortable: false,
    suppressHeaderMenuButton: true,
  },
  createdByColDef(CreatedByCellRenderer, {
    comparator: disabledComparator,
    field: 'created_at',
    suppressHeaderMenuButton: true,
  }),
  lastUpdateColDef({
    comparator: disabledComparator,
    field: 'updated_at',
    suppressHeaderMenuButton: true,
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
